import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule/Schedule';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  Job,
  JobAttributeValue,
  JobKeys,
  JobRelationshipKeys,
  JobSerializedEntity,
  JobState,
} from './types';

export const jobsAdapter = createEntityAdapter<Job>();

export const initialState: JobState = {
  ...jobsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: jobsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase('resources/fetch/pending', (state, action: any) => {
      if (action.meta?.arg?.name === 'schedule') {
        state.status = 'pending';
      }
    });
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state, action: ScheduleFetchAction<JobSerializedEntity>) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const jobs = action.payload.included
              .filter(
                (entity): entity is JobSerializedEntity => entity.type === 'job'
              )
              .map(entity => ({
                id: entity.id,
                type: entity.type,
                level: entity.attributes.level,
                defaultRoleId: entity.attributes.defaultRoleId,
                wageRate: entity.attributes.wageRate,
                scheduleConfirmation: entity.attributes.scheduleConfirmation,
                ptoCategories: entity.attributes.ptoCategories,
                archivedAt: entity.attributes.archivedAt,
                user: entity.relationships?.user?.data,
                location: entity.relationships?.location?.data,
                roleWages: entity.relationships?.roleWages?.data,
              }));
            jobsAdapter.setAll(state, jobs);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const {
  selectById: selectJobById,
  selectIds: selectJobIds,
  selectEntities: selectJobEntities,
  selectAll: selectAllJobs,
  selectTotal: selectTotalJobs,
} = jobsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const { removeAll } = slice.actions;
export const { reducer } = slice;

export const selectJobsStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectJobAttribute = <T extends keyof Job>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}) =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectJobById,
    id,
  });

export const selectJobRelationshipId = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: JobRelationshipKeys;
}): EntityId | EntityId[] | undefined => {
  const job = selectJobById(state, id);
  if (!job) return undefined;

  if (relationship === 'roleWages') {
    const roleWages = job.roleWages;
    return Array.isArray(roleWages)
      ? roleWages.map(item => item.id)
      : undefined;
  }

  if (relationship === 'user') {
    return job.user?.id;
  }

  if (relationship === 'location') {
    return job.location?.id;
  }

  return undefined;
};

export const selectJobsWhereAttribute = createCachedSelector(
  [
    selectAllJobs,
    (
      _state: RootState,
      { attribute, value }: { attribute: JobKeys; value: JobAttributeValue }
    ) => ({
      attribute,
      value,
    }),
  ],
  (
    jobs: Job[],
    { attribute, value }: { attribute: JobKeys; value: JobAttributeValue }
  ) => jobs.filter((job: Job) => job[attribute] === value)
)(
  (_, params?: { attribute: JobKeys; value: JobAttributeValue }) =>
    `selectJobsWhereAttribute-${params?.attribute || 'unknown'}-${
      params?.value || 'unknown'
    }`
);
